import {
  CellContext,
  ColumnDef,
  ColumnDefTemplate,
  RowSelectionState,
  createColumnHelper,
} from "@tanstack/react-table";
import { PTable } from "./Table/PTable";
import { useContext, useMemo, useState } from "react";
import { CellValueUnion, FormFieldValueGroupUI, cellValueFromGQL, displayDate, parseDate } from "Backend";
import { Cell, CellType, TagView } from "generated/graphql";
import { FormFieldContext } from "contexts/FormFieldContext";
import { Checkbox } from "@mui/material";
import { TagDisplay } from "./BetterResponseTable/TagDisplay";
import { ValueGroupStar } from "./FormField/ValueGroupStar";
import { ReferenceIcon } from "./BetterResponseTable/ReferenceIcon";
import { TagFilters } from "./BetterResponseTable/TagFilters";
import { useFilteredValueGroups } from "helpers/hooks/useFilteredValueGroups";
import { EntryContext } from "./tags/types";
import { ContextDialog } from "./ContextDialog/ContextDialog";
import { ContextMode } from "./ContextDialog/types";

type NewFFVG = FormFieldValueGroupUI & { availableTags: TagView[] };

const columnHelper = createColumnHelper<NewFFVG>();
// TODO: figure out how to use the builtin column meta for this
const columnBase = (meta: ColumnMeta) => [
  columnHelper.display({
    id: "actions",
    minSize: 105,
    enableResizing: false,
    header: ({ table }) => (
      <span className="inline-flex items-center gap-2">
        <Checkbox
          className="mx-0 px-0 py-0.5"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      </span>
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          className="mx-0 px-0 py-0.5"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
        <ValueGroupStar group={row.original} />
        <ReferenceIcon row={row.original} onClick={() => meta.showEntryViewer(row.original)} />
        <TagDisplay.Dense
          valueGroupSerialId={row.original.serialId}
          appliedTags={row.original.tags}
          availableTags={row.original.availableTags || []}
        />
      </div>
    ),
  }),
];

function DefaultCell<A>(props: CellContext<A, unknown>) {
  return <span>{props.getValue()?.toString()}</span>;
}

function DateCell(props: CellContext<NewFFVG, CellValueUnion>) {
  const value = props.getValue();
  const date = parseDate(value as string);
  return <span>{displayDate(date)}</span>;
}

const CellValueTypeMap: { [key in CellType]: ColumnDefTemplate<CellContext<NewFFVG, CellValueUnion>> } = {
  [CellType.Boolean]: DefaultCell,
  [CellType.Date]: DateCell,
  [CellType.Text]: DefaultCell,
  [CellType.Float]: DefaultCell,
  [CellType.Integer]: DefaultCell,
  [CellType.Select]: DefaultCell,
  [CellType.MultiSelect]: DefaultCell,
};

const cellValueAccessor = (cellIndex: number) => (c: NewFFVG) => {
  const cell = c.values[cellIndex];
  return cellValueFromGQL(cell!.value);
};

interface ColumnMeta {
  showEntryViewer: (vg: NewFFVG) => void;
}

function columnsFromCells(
  cells: Cell[],
  columnBase: (meta: ColumnMeta) => ColumnDef<NewFFVG, CellValueUnion>[],
  meta: ColumnMeta
): ColumnDef<NewFFVG, CellValueUnion>[] {
  return columnBase(meta).concat(
    cells.map((cell, index) => {
      return {
        id: cell.systemName,
        header: cell.name,
        accessorFn: cellValueAccessor(index),
        cell: CellValueTypeMap[cell.type] || DefaultCell,
      };
    })
  );
}

export function NewEntryTable({ defaultEntryContext }: { defaultEntryContext?: EntryContext }) {
  const [selectedResponses, setSelectedResponses] = useState<RowSelectionState>({});
  const [entryContext, setEntryContext] = useState<EntryContext | undefined>(defaultEntryContext);
  const ffContext = useContext(FormFieldContext);
  const { availableTags, filteredValueGroups, tagFilters, setTagFilters, tagCounts } = useFilteredValueGroups();
  const showEntryViewer = (vg: NewFFVG) => {
    setEntryContext({ valueGroupId: vg.id, referenceId: vg.references[0]?.id });
  };
  const columns = useMemo(
    () => columnsFromCells(ffContext.formField.cells, columnBase, { showEntryViewer }),
    [ffContext.formField.id, setEntryContext]
  );

  return (
    <div className="grid gap-2">
      <div className="flex gap-2 ">
        <TagFilters
          availableTags={availableTags}
          tagFilters={tagFilters}
          setTagFilters={setTagFilters}
          tagCounts={tagCounts}
        />
      </div>
      <PTable
        data={filteredValueGroups}
        columns={columns}
        state={{ rowSelection: selectedResponses }}
        onRowSelectionChange={setSelectedResponses}
        getRowId={(row, _) => row.id.toString()}
        defaultColumn={{ size: 150, minSize: 50 }}
      />
      {entryContext && (
        <ContextDialog
          open={true}
          mode={ContextMode.EntryViewer}
          onClose={() => setEntryContext(undefined)}
          componentProps={{
            entryContext,
            setEntryContext,
            onClose: () => setEntryContext(undefined),
            isLinkable: true,
          }}
        />
      )}
    </div>
  );
}
